/**
 * RetentionRunsTable, sheet 43 callout 8 (mission 10 WP16 T094; FR-017, FR-006's screen half, FR-024,
 * NFR-011; D.2, D.8, NF.9; squad U26; style guide §7.7, §9).
 *
 * Every run still holding Class 1, in the window read's order (`due_at`, id): the short id in mono, the
 * completion and deletion dates in mono UTC `<time datetime>`, and the "still holds" labels. A null `due_at`
 * (retention 0 while the first transmit request is pending) reads `runsWaitingForTransmission`, never a date
 * (U26). Every column is a `<th scope="col">` (TableHead's default). Older pages come by the opaque cursor
 * (`nextPage`); the table container scrolls horizontally, never the page (C-015).
 * With no run and no cursor left, `runsTableEmpty` takes the table's place (§7.14; WP16 cycle 2, review finding 4).
 */
import { ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { RetentionHold, RetentionRun } from '@/lib/api/retention'
import { Mono } from '@/lib/format'
import { copyText, type TextKey } from '@/lib/provisional-copy'

import { UtcDate } from './dates'

const HOLD_LABEL: Record<RetentionHold, TextKey> = {
  upload: 'holdsUpload',
  generated: 'holdsGenerated',
  findings: 'holdsFindings',
  validation_report: 'holdsValidationReport',
  conversation: 'holdsConversation',
  rejection_text: 'holdsRejectionText',
}

const HEADINGS = ['runsTableHeadings.run', 'runsTableHeadings.completed', 'runsTableHeadings.due', 'runsTableHeadings.holds'] as const

export interface RetentionRunsTableProps {
  /** Every page read so far, in order. */
  runs: readonly RetentionRun[]
  hasNextPage: boolean
  loadingNext: boolean
  onNextPage: () => void
}

export function RetentionRunsTable({ runs, hasNextPage, loadingNext, onNextPage }: RetentionRunsTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{copyText('runsTableTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        {runs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                {HEADINGS.map((key) => (
                  <TableHead key={key}>{copyText(key)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>
                    <Mono className="text-body-sm">{run.short_id}</Mono>
                  </TableCell>
                  <TableCell className="text-body-sm">
                    {/* Mission 10 WP17 (WP07 review): a legacy run that recorded no completion is blank. */}
                    {run.completed_at === null ? null : <UtcDate iso={run.completed_at} mono />}
                  </TableCell>
                  <TableCell className="text-body-sm">
                    {run.due_at === null ? copyText('runsWaitingForTransmission') : <UtcDate iso={run.due_at} mono />}
                  </TableCell>
                  <TableCell className="text-body-sm">{run.holds.map((h) => copyText(HOLD_LABEL[h])).join(', ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : hasNextPage ? null : (
          <p data-runs-empty="" className="text-body text-navy-600">
            {copyText('runsTableEmpty')}
          </p>
        )}
        {hasNextPage ? (
          <div className="mt-3 flex justify-end">
            <Button variant="secondary" size="sm" loading={loadingNext} onClick={onNextPage}>
              {copyText('nextPage')}
              <ChevronRight aria-hidden />
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
